import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './index.scss';
import { CustomButton, CustomTable } from '../../shared/uiComponents';
import { useCheckPermission } from '../../shared/Helpers/hooks';
import { PlusIcon } from '../../shared/Assets/svg';
import { PEARL } from '../../shared/Helpers/colors';
import { Users } from '../../shared/Assets/menuIcons';
import SearchBar from '../../components/SearchBar';
import { AdminTypes } from '../../components/Menu';
import { PERMISSIONS } from '../../App/constants';
import { ClientFilters, Headers, TableBody } from './tableInfo';
import MobileView from './mobileView';
import MobileSearchHeader from './mobileSearchHeader';

import { DispatchProperties, useSelector } from '../../redux/store';
import { getClients } from '../../redux/State/clientSlice/clientSlice';
import { getCaseCoordinators, getUserClients } from '../../redux/State/clientSlice/userClientSlice';
import { PageHeader } from '../../components/PageHeader';

const Clients = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();

	const page = params.get('page') || '1';
	const pageSize = params.get('pageSize') || '8';
	const searchString = params.get('search') || '';
	const orderBy = params.get('orderBy') || '';
	const direction = params.get('direction') || '';
	const status = params.get('status');
	const caseCoordinatorId = params.get('caseCoordinatorId') || '';
	const funderId = params.get('funderId') || '';
	const hasActiveFunderAuthorization = params.get('hasActiveFunderAuthorization') || '';

	const { permissionGranted } = useCheckPermission(PERMISSIONS.CLIENT.CREATE);

	const userId = useSelector((state) => state.account.user.id);
	const role = useSelector((state) => state.account.role);
	const clientsData = useSelector((state) => state.client.clients);
	const userClients = useSelector((state) => state.userClient.userClients);
	const loading = useSelector((state) => state.client.loading);
	const loadingUserClients = useSelector((state) => state.userClient.loadingGetUserClients);

	const canHaveAction = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs;

	const addClientHandler = () => navigate('/clients/new');

	useEffect(() => {
		if (role?.section.id !== AdminTypes.admin) return;
		dispatch(getCaseCoordinators());
	}, [dispatch, role]);

	useEffect(() => {
		if (canHaveAction) {
			dispatch(
				getClients({
					page,
					pageSize,
					searchString,
					orderBy,
					direction,
					status,
					caseCoordinatorId,
					funderId,
					hasActiveFunderAuthorization,
				}),
			);

			return;
		}

		if (!userId) return;
		dispatch(
			getUserClients({
				userId,
				page,
				pageSize,
				searchString,
				orderBy,
				direction,
				status,
			}),
		);
	}, [page, pageSize, searchString, orderBy, direction, status, caseCoordinatorId, funderId, hasActiveFunderAuthorization, userId, dispatch, canHaveAction]);

	return (
		<div className="clientsWrapper">
			<PageHeader
				icon={<Users />}
				title="Clients"
				headerContent={
					<>
						<SearchBar hasFilter style={{ marginTop: 0, background: PEARL.QUATERNARY }} />
						{permissionGranted && (
							<CustomButton title="Add Client" onClick={addClientHandler} startIcon={<PlusIcon />} />
						)}
						<ClientFilters />
					</>
				}
			/>

			{/* Mobile Header */}
			<MobileSearchHeader />

			<CustomTable
				headers={Headers}
				data={canHaveAction ? clientsData : userClients}
				TableBody={TableBody}
				loading={loading || loadingUserClients}
			/>
			{canHaveAction ? (
				<MobileView {...clientsData} loading={loading} />
			) : (
				<MobileView {...userClients} loading={loadingUserClients} />
			)}
		</div>
	);
};

export default Clients;
